import { Link } from "@tanstack/react-router";

/**
 * Phase 6 — Internal linking mesh for the Pakistan hub.
 * Distributes link equity across the karat, unit, urdu, comparison,
 * historical and syndication routes. Drop it into any /pakistan/*
 * leaf below the main content.
 */

type Group = { heading: string; links: Array<{ to: string; label: string }> };

const GROUPS: Group[] = [
  {
    heading: "By karat",
    links: [
      { to: "/pakistan/24k", label: "24K gold" },
      { to: "/pakistan/22k", label: "22K gold" },
      { to: "/pakistan/21k", label: "21K gold" },
      { to: "/pakistan/18k", label: "18K gold" },
    ],
  },
  {
    heading: "By unit",
    links: [
      { to: "/pakistan/per-tola", label: "Per Tola" },
      { to: "/pakistan/per-10-gram", label: "Per 10 Gram" },
      { to: "/pakistan/per-gram", label: "Per Gram" },
      { to: "/pakistan/per-ratti", label: "Per Ratti" },
    ],
  },
  {
    heading: "Context",
    links: [
      { to: "/pakistan/yesterday", label: "Yesterday vs today" },
      { to: "/pakistan/history", label: "30-day trend" },
      { to: "/pakistan/vs/india", label: "Pakistan vs India" },
      { to: "/pakistan/calculator", label: "Price calculator" },
      { to: "/pakistan/zakat-nisab", label: "Zakat nisab" },
    ],
  },
  {
    heading: "اردو / Urdu",
    links: [
      { to: "/pakistan/urdu", label: "پاکستان سونا ریٹ" },
      { to: "/pakistan/urdu/karachi", label: "کراچی" },
      { to: "/pakistan/urdu/lahore", label: "لاہور" },
      { to: "/pakistan/urdu/islamabad", label: "اسلام آباد" },
    ],
  },
];

export function PakistanNavigator({ exclude }: { exclude?: string } = {}) {
  return (
    <nav
      aria-label="Pakistan gold rate — related pages"
      className="mt-10 rounded-xl border border-border bg-muted/40 p-5 text-sm"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Pakistan gold rate — full index</h2>
        <Link to="/pakistan" className="text-xs text-primary hover:underline">
          Hub →
        </Link>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-5 sm:grid-cols-4">
        {GROUPS.map((g) => (
          <div key={g.heading}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {g.heading}
            </p>
            <ul className="mt-2 space-y-1.5">
              {g.links
                .filter((l) => l.to !== exclude)
                .map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-primary hover:underline">
                      {l.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

export type Crumb = { name: string; href: string };

/**
 * Renders a visible breadcrumb trail plus a BreadcrumbList JSON-LD
 * schema so Google can build a rich-result trail on SERP.
 */
export function PakistanBreadcrumb({ items }: { items: Crumb[] }) {
  const full: Crumb[] = [
    { name: "Home", href: "https://drhint.com/" },
    ...items,
  ];
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: full.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.href,
    })),
  };
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        {full.map((c, i) => (
          <span key={c.href}>
            {i > 0 && " › "}
            {i === full.length - 1 ? (
              <span className="text-foreground">{c.name}</span>
            ) : (
              <a href={c.href} className="hover:underline">
                {c.name}
              </a>
            )}
          </span>
        ))}
      </nav>
    </>
  );
}

/** Injects only the BreadcrumbList JSON-LD (use when the page already
 *  renders its own visible crumb trail). */
export function PakistanBreadcrumbSchema({ items }: { items: Crumb[] }) {
  const full: Crumb[] = [{ name: "Home", href: "https://drhint.com/" }, ...items];
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: full.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.href,
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}